import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateUserMutation, useUserQuery } from "../../services/userApi";
import WareHouseDWUser from "../Common/CustomSelect/WareHouseDWUser";
import Header from "../Common/Header/Header";
import SideBar from "../Common/SideBar/SideBar";
import { JwtReValidate, signInUser } from "../Utility/Auth";
import { notify } from "../Utility/Notify";

const UpdateUser = () => {
  const auth = signInUser(); // Current logged-in user
  const { id } = useParams(); // Get the user ID from the URL
  const [selectedUser, setSelectedUser] = useState(null); // State for the user being updated

  // Fetch user data based on ID
  const { data, error, isLoading } = useUserQuery(id);
  const [wh, setWh] = useState("");

  // Form handling using react-hook-form
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      _id: "",
      name: "",
      username: "",
      email: "",
      password: "",
      phone: "",
      status: "",
      type: "",
      warehouse: "",
    },
  });

  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  // Populate form when data is available
  useEffect(() => {
    if (data) {
      reset({
        _id: data._id,
        name: data.name,
        username: data.username,
        phone: data.phone,
        email: data.email,
        password: "",
        status: data.status,
        type: data.type,
        warehouse: data.warehouse,
      });
      setWh(data.warehouse);
      setSelectedUser(data); // Set the selected user
    }
  }, [data, reset]);

  // Handle warehouse change
  const handleOnchangeWareHouse = (e) => {
    setWh(e.option);
  };

  // Handle form submission
  const updateHandler = async (formData) => {
    const resolvedWh =
      wh ||
      formData.warehouse ||
      (typeof auth?.warehouse === "string" ? auth.warehouse : auth?.warehouse?._id) ||
      (typeof data?.warehouse === "string" ? data.warehouse : data?.warehouse?._id);

    const updatedData = { ...formData, warehouse: resolvedWh };

    if (!updatedData.password) delete updatedData.password;

    if (!resolvedWh) {
      notify("Please select a warehouse");
      return;
    }

    const response = await updateUser(updatedData);
    if (response?.error) {
      notify(response.error?.data?.message || "Failed to update user", "error");
    } else {
      await JwtReValidate();
      notify("User Update Successful!", "success");
      navigate("/user");
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-2">
          <SideBar />
        </div>
        <div className="col-md-10">
          <Header
            title={`Update User: ${selectedUser?.name || "Loading..."}`}
          />
          <div className="row mt-md-5 pt-3">
            <div className="col-md-6 offset-md-3">
              <Form onSubmit={handleSubmit(updateHandler)}>
                {/* Name Field */}
                <Form.Group className="mb-3" controlId="formBasicName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    {...register("name", { required: true })}
                    type="text"
                    placeholder="Name"
                  />
                </Form.Group>

                {/* Username Field */}
                <Form.Group className="mb-3" controlId="formBasicUsername">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    {...register("username", { required: true })}
                    type="text"
                    placeholder="Username"
                  />
                </Form.Group>

                {/* Email Field */}
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    {...register("email", { required: true })}
                    type="email"
                    placeholder="Enter email"
                  />
                  <Form.Text className="text-muted">
                    We'll never share your email with anyone else.
                  </Form.Text>
                </Form.Group>

                {/* Phone Field */}
                <Form.Group className="mb-3" controlId="formBasicPhone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    {...register("phone")}
                    type="text"
                    placeholder="Phone"
                  />
                </Form.Group>

                {/* Password Field - Condition for Admin/Manager */}
                {(auth.type === "admin" ||
                  (auth.type === "manager" &&
                    selectedUser?.type !== "admin")) && (
                  <Form.Group className="mb-3" controlId="formBasicPassword">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      {...register("password")}
                      type="password"
                      placeholder="Password"
                    />
                    <Form.Text className="text-muted">
                      We'll never share your password with anyone else.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Warehouse Field */}
                <Form.Group className="mb-3" controlId="formBasicWarehouse">
                  <Form.Label>Warehouse</Form.Label>
                  <WareHouseDWUser
                    name="warehouse"
                    handleOnChange={handleOnchangeWareHouse}
                    warehouse={wh || 0}
                  />
                </Form.Group>

                {/* User Type Field */}
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formBasicType">
                      <Form.Label>User Type</Form.Label>
                      <Form.Select
                        {...register("type")}
                        disabled={auth?.type !== "admin" && auth?.type !== "owner"}
                      >
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="POS">POS</option>
                        <option value="ecom">Ecom Manager</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="accounts">Accounts</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* Status Field */}
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formBasicStatus">
                      <Form.Label>Status</Form.Label>
                      <Form.Select {...register("status")}>
                        <option value="active">Active</option>
                        <option value="suspend">Suspend</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>

                {/* Submit Button */}
                <Button variant="dark" type="submit">
                  <Icons.PlusOutline size={20} /> Update User
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUser;
